import { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button, Modal, ModalYesNo, TextField } from '@scripsteam/scrips-signal-ds';
import { DatePickerField } from '@/shared/ui/components';
import { Switch, Select } from '@scripsteam/scrips-signal-ds';
import { useAuthStore } from '@/auth/auth-store';
import type { OrgLocation } from '@/auth/auth-api';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import {
  useAddDoctorWorkingHours,
  useDeleteDoctorWorkingHours,
  useUpdateDoctorWorkingHours,
} from '@/settings/services';
import { useUpcomingAppointmentCount } from '@/settings/services/use-upcoming-appointment-count';
import {
  disableLocationWarningCopy,
  findDisabledLocations,
  joinDisabledLocationNames,
} from './disable-location-guard';
import type {
  DoctorWorkingHour,
  DoctorWorkingHourSlot,
} from '@/types/settings-api';
import {
  DOCTOR_WH_TYPES,
  HOURS_LIST,
  MINUTES_LIST,
  WEEKDAYS,
} from './working-hours-constants';
import {
  applyDayOffToggle,
  applySlotRemove,
  makeEmptySlot,
} from './working-hours-slot-helpers';

/**
 * Working Hours edit dialog — PROD-762 (Add + Edit + Delete, fully wired).
 *
 * Flutter parity: `doctor_working_hours_form_widget.dart` +
 * `add_working_hours_widget.dart` (the per-day slot grid + add/remove
 * row affordance + hour/minute pickers + Location (service type) multi-select).
 *
 * Covers:
 *   - Hour / minute picker primitives (`HourSelect`, `MinuteSelect`)
 *   - Per-day slot row with start/end hour+minute + remove (DaySlotRow)
 *   - Per-day group with dayOff toggle + Add Slot button (DayGroup)
 *   - Top-level dialog: practice (picker on Add / read-only on Edit) +
 *     name + type + dates + Location multi-select (type 1 only) + 7 day groups
 *   - Add / Edit / Delete via useAddDoctorWorkingHours /
 *     useUpdateDoctorWorkingHours / useDeleteDoctorWorkingHours
 *   - Edit flow: opens with a fetched canonical record from
 *     `useDoctorWorkingHourById` (parent passes the `initial` prop)
 *
 * Location (service type) multi-select (Flutter
 * `doctor_working_hours_form_widget.dart:557`): options come from the
 * logged-in user's `organizationLocations` (auth store — the same source
 * as Flutter's `userData.organizationLocations`). Only shown for type 1
 * (Work). A location is "enabled" when present in the draft's `locations[]`
 * (matched by `id`); the wire ships `locations: [{id,name}…]` + `locationIds:
 * null` (curl-verified shape, see use-add-doctor-working-hours.ts). Save
 * requires ≥1 enabled (Flutter "Please Enable atleast One Location"). On Add
 * the field defaults to all org locations enabled, mirroring Flutter's
 * null→all-org-locations default.
 *
 * State model:
 *   The draft is grouped client-side by `weekDay` (Map<weekday, slots[]>)
 *   for editing, then flattened back to a single `workingHoursRequest[]`
 *   on Save. Each existing slot keeps its server-allocated GUID `id`;
 *   newly-added slots get `id: ""` per Flutter convention (backend
 *   allocates server-side, same invariant proved across other PROD-761
 *   typeaheads). Removing a persisted slot flips `isDeleted: true`
 *   and KEEPS it in the array — the backend won't archive a row that
 *   silently disappears.
 */
/**
 * Practice option for the Add-mode picker. The picker is hidden on Edit
 * (the practice is fixed at creation per Flutter `doctor_working_hours_
 * form_widget.dart`). The shape is the subset of `PracticeList` we
 * actually read — keeping the type narrow lets callers pass either the
 * full list type or a slimmer derived projection.
 */
export interface PracticeOption {
  practiceSetupId: string | null;
  practiceName: string | null;
}

export interface WorkingHoursEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Existing canonical record (Edit) or `null` (Add). When `null`, the
   * dialog synthesizes a blank template draft and shows a practice
   * picker instead of the read-only practice banner.
   */
  initial: DoctorWorkingHour | null;
  /**
   * Doctor setup id — drives mutation hook cache invalidation AND seeds
   * the new draft's `doctorId` on Add. REQUIRED for Add mode since the
   * blank template has no doctorId to fall back on; optional for Edit
   * (defaults to `draft.doctorId`, which is the same value).
   */
  doctorSetupId?: string;
  /**
   * Practitioner-scoped practice list — feeds the Add-mode practice
   * picker. Required for Add mode; ignored on Edit. Parent typically
   * passes the result of `usePractitionerPractices(doctorSetupId).data`.
   */
  practiceOptions?: PracticeOption[];
}

/**
 * Build a blank-template draft for Add mode. Mirrors Flutter
 * `doctor_working_hours_form_widget.dart:35` + `:774` default state:
 *
 *   - type defaults to 1 (Work)
 *   - startDate / endDate are EMPTY strings — user MUST pick from the
 *     date inputs before Save enables. Flutter convention across all
 *     chart-tab Add flows is `null` initial value + placeholder visible
 *     + Save validation `startDate == null ? "Please select start
 *     date" : ""`. We mirror that here with empty-string sentinels
 *     (DateField already handles `""` as "show empty input").
 *   - empty practice (user must pick before Save validates)
 *   - empty slots (user picks days + hours)
 *
 * Why NOT pre-populate with `today → today+1yr`: doing so silently
 * commits the user to a 1-year span without any explicit choice. The
 * captured Add cURL had ~2-year span, the user picked that explicitly.
 * Defaulting hides the field and creates accidental long-term commits.
 *
 * `DateTime.now()` IS used elsewhere in chart tabs — but only as:
 *   - a `lastDate` constraint (e.g. onset can't be future) — not a value
 *   - an audit / action timestamp (createdDate, un-resolve action time)
 *     — not a user-picked value
 *
 * Wire-shape sentinels are baked in: workingHourId "", practiceName "",
 * organizationId / Timezone null. See `use-add-doctor-working-hours.ts`
 * for the full contract.
 */
function makeBlankDraft(doctorSetupId: string): DoctorWorkingHour {
  return {
    practiceId: '',
    doctorId: doctorSetupId,
    workingHourId: '',
    profileName: '',
    practiceName: '',
    type: 1,
    room: null,
    startDate: '',
    endDate: '',
    followupTimeSlot: '',
    timeSlot: '',
    organizationId: null as unknown as string,
    organizationTimezone: null as unknown as string,
    locationIds: null,
    locations: [],
    workingHoursRequest: [],
  };
}

export function WorkingHoursEditDialog({
  open,
  onOpenChange,
  initial,
  doctorSetupId,
  practiceOptions,
}: WorkingHoursEditDialogProps) {
  const isAdd = initial === null;
  // Location (service type) options — the logged-in user's org locations,
  // same source Flutter's working-hours form reads (userData.organizationLocations).
  const orgLocations = useAuthStore((s) => s.user?.organizationLocations) ?? [];
  const organizationId = useAuthStore((s) => s.user?.organizationId);
  const seed = initial ?? makeBlankDraft(doctorSetupId ?? '');
  const [draft, setDraft] = useState<DoctorWorkingHour>(seed);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  // F-PRS-117: disable-location appointment-count guard. Holds the
  // confirmation copy + the in-flight count while the user decides.
  const [disableLocConfirm, setDisableLocConfirm] = useState<{
    names: string;
    count: number;
  } | null>(null);

  const cacheId = doctorSetupId ?? draft.doctorId;
  const addMutation = useAddDoctorWorkingHours(cacheId);
  const updateMutation = useUpdateDoctorWorkingHours(cacheId);
  const deleteMutation = useDeleteDoctorWorkingHours(cacheId);
  const apptCountMutation = useUpcomingAppointmentCount();

  const isEdit = !!draft.workingHourId;
  const isMutating =
    addMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    apptCountMutation.isPending;

  // Seed the draft during render when the dialog (re)opens or targets a new
  // record — adjusting state during render rather than in an effect avoids the
  // synchronous-setState-in-effect cascading-render warning (PROD-967 pattern).
  // `seedKey` is the open record's identity (or null when closed); when it
  // changes we re-seed and converge in one extra render.
  const seedKey = open ? (initial ? `edit:${initial.workingHourId}` : 'add') : null;
  const [seededKey, setSeededKey] = useState<string | null>(null);
  if (seedKey !== seededKey) {
    setSeededKey(seedKey);
    if (seedKey !== null) {
      if (initial) {
        setDraft(initial);
      } else {
        // Add mode: blank template, but default all org locations enabled —
        // mirrors Flutter defaulting `locations` to `organizationLocations`
        // when null (doctor_working_hours_form_widget.dart:544). The user can
        // deselect; the ≥1 validation gates Save.
        const blank = makeBlankDraft(doctorSetupId ?? '');
        blank.locations = orgLocations.map((l) => ({ id: l.id, name: l.name }));
        setDraft(blank);
      }
    }
  }

  // A location is enabled when present in the draft's locations[] (matched by
  // id). Toggling writes the {id,name} subset the wire expects and pins
  // locationIds to null (curl-verified shape).
  const enabledLocationIds = new Set(
    draft.locations.map((l) => l.id).filter((id): id is string => !!id),
  );
  const toggleLocation = (loc: OrgLocation, on: boolean) => {
    setDraft((d) => ({
      ...d,
      locationIds: null,
      locations: on
        ? [...d.locations.filter((l) => l.id !== loc.id), { id: loc.id, name: loc.name }]
        : d.locations.filter((l) => l.id !== loc.id),
    }));
  };
  // Flutter "Please Enable atleast One Location" — only enforced for type 1 (Work).
  const locationsInvalid = draft.type === 1 && enabledLocationIds.size === 0;

  // Group the draft's slot array by weekday. The render order follows
  // WEEKDAYS (Monday..Sunday) so slot order from the server doesn't
  // shuffle the UI.
  const slotsByDay = useMemo(() => {
    const map = new Map<string, DoctorWorkingHourSlot[]>();
    for (const day of WEEKDAYS) {
      map.set(day, []);
    }
    for (const slot of draft.workingHoursRequest ?? []) {
      if (slot.isDeleted) continue;
      const list = map.get(slot.weekDay);
      if (list) list.push(slot);
    }
    return map;
  }, [draft.workingHoursRequest]);

  const updateSlotsForDay = (
    weekday: string,
    next: DoctorWorkingHourSlot[],
  ) => {
    // Flatten back to a single array. Keep soft-deleted entries for
    // other days untouched. Replace this weekday's visible slots with
    // `next`, while preserving any soft-deleted rows for this same
    // weekday so the server's archive contract still fires.
    const all = draft.workingHoursRequest ?? [];
    const otherDays = all.filter(
      (s) => s.weekDay !== weekday || s.isDeleted,
    );
    setDraft({
      ...draft,
      workingHoursRequest: [...otherDays, ...next],
    });
  };

  // F-PRS-117: locations present on the original record but absent from the
  // draft are "disabled" (Flutter marks them `isArchived` and keeps them in
  // the array; React removes them from `locations[]`). On Edit, disabling a
  // location that still has upcoming appointments must warn the user first
  // (Flutter `doctor_schedule_tab.dart:469-533`).
  const disabledLocations = findDisabledLocations(
    initial?.locations,
    draft.locations,
  );

  const doSave = () => {
    const mutation = isEdit ? updateMutation : addMutation;
    mutation.mutate(draft, { onSuccess: () => onOpenChange(false) });
  };

  const handleSaveClick = () => {
    // Add mode, or nothing disabled → save directly.
    if (!isEdit || disabledLocations.length === 0) {
      doSave();
      return;
    }
    // Flutter joins the archived location names with " ," (verbatim spacing)
    // and fetches the count for the FIRST archived location ONLY
    // (`doctor_schedule_tab.dart:481-485` uses `.first.id`). This is exact
    // Flutter parity, not a limitation we introduced: when multiple
    // locations are disabled at once, the count shown is for the first, but
    // the warning message lists ALL disabled names (Samer #492 §2).
    const names = joinDisabledLocationNames(disabledLocations);
    apptCountMutation.mutate(
      {
        providerId: draft.doctorId,
        locationId: disabledLocations[0]?.id,
        practiceId: draft.practiceId,
        // organizationId is sourced from auth context (useAuthStore.user),
        // never a prop — preserves tenant isolation (Samer #492 §4).
        organizationId,
      },
      {
        onSuccess: (count) => setDisableLocConfirm({ names, count }),
        onError: (err) => {
          // The warning is advisory, so a failed count fetch still falls
          // through to a direct save rather than blocking the user — but it
          // is NOT swallowed silently (Samer #492 §1): surface it for ops.
          // (api-client also reports failures via the global error pipeline;
          // this is an explicit breadcrumb at the call site.)
          // eslint-disable-next-line no-console
          console.error(
            '[F-PRS-117] upcoming-appointment count fetch failed; saving without the disable-location warning',
            err,
          );
          doSave();
        },
      },
    );
  };

  return (
    <>
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={isAdd ? 'Add Working Hours' : 'Edit Working Hours'}
      size="xl"
      footer={<><div className="flex items-center gap-2">
            <Button
              type="button"
              disabled={
                isMutating ||
                !draft.profileName.trim() ||
                (isAdd && !draft.practiceId) ||
                // Flutter parity: doctor_working_hours_form_widget.dart:796-799
                // forces start+end date selection. Across all chart-tab Add
                // flows in Flutter the convention is the same — date inputs
                // start null, Save validates non-null. Mirror via empty-string
                // check (blank template uses "" for both).
                !draft.startDate ||
                !draft.endDate ||
                // Flutter "Please Enable atleast One Location" — type 1 only.
                locationsInvalid
              }
              onClick={handleSaveClick}
            >
              {isMutating && (addMutation.isPending || updateMutation.isPending)
                ? 'Saving…'
                : 'Save'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={isMutating}
            >
              Cancel
            </Button>
          </div>
          {/* Delete is meaningful only for existing rows — never for an
           *  unsaved draft. Hidden (not just disabled) on Add to match
           *  Flutter `edit_practice_working_hours_widget` (Delete affordance
           *  only shows when `workingHourId != null`). */}
          {isEdit && (
            <Button
              type="button"
              variant="tertiary"
              disabled={isMutating}
              className="text-status-error"
              onClick={() => setConfirmDeleteOpen(true)}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          )}</>}
      footerAlign="between"
    >
        

        <div data-slot="settings-dialog-body" className="space-y-4 py-2 max-h-overlay-body overflow-y-auto scrollbar-hide">
          {isAdd ? (
            <PracticeField
              value={draft.practiceId}
              options={practiceOptions ?? []}
              onChange={(practiceId) => setDraft({ ...draft, practiceId })}
            />
          ) : (
            <PracticeReadOnly profile={draft} />
          )}

          <div className="grid grid-cols-2 gap-4">
            <NameField
              value={draft.profileName}
              onChange={(v) => setDraft({ ...draft, profileName: v })}
            />
            <TypeField
              value={draft.type}
              onChange={(v) => setDraft({ ...draft, type: v })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <DateField
              label="Start date"
              value={draft.startDate}
              onChange={(v) => setDraft({ ...draft, startDate: v })}
            />
            <DateField
              label="End date"
              value={draft.endDate}
              onChange={(v) => setDraft({ ...draft, endDate: v })}
            />
          </div>

          {draft.type === 1 && (
            <LocationsField
              options={orgLocations}
              enabledIds={enabledLocationIds}
              onToggle={toggleLocation}
            />
          )}

          <div className="border-t border-border-hairline pt-4">
            <FieldLabel stacked>Weekly schedule</FieldLabel>
            <div className="mt-2 space-y-1">
              {WEEKDAYS.map((day) => (
                <DayGroup
                  key={day}
                  weekday={day}
                  slots={slotsByDay.get(day) ?? []}
                  onChange={(next) => updateSlotsForDay(day, next)}
                />
              ))}
            </div>
          </div>

        </div>

        
      </Modal>

      {/* Destructive-action confirmation — shadcn AlertDialog. Replaces
       *  the prior `window.confirm` per @samer PR #220 review: window.confirm
       *  is blocked in some embedded WebView contexts (relevant if a
       *  screen ever renders inside a Flutter WebView shell during the
       *  React-rewrite transition). Reusing the same pattern as
       *  `document-row.tsx`'s archive flow + scheduling block-availability
       *  delete + macros so the prompt UX stays consistent module-wide.
       *
       *  Nesting note: the AlertDialog sits at the same JSX level as the
       *  outer Dialog (NOT inside DialogContent) so Radix's focus-trap
       *  in the outer Dialog doesn't fight the AlertDialog's own trap.
       *  This is the same nesting pattern document-row.tsx + the macro
       *  dialog use. */}
      <ModalYesNo
        open={confirmDeleteOpen}
        onCancel={() => setConfirmDeleteOpen(false)}
        title="Delete this working-hours profile?"
        message="Existing appointments are unaffected; the profile will be archived and removed from this practitioner's schedule."
        confirmLabel={deleteMutation.isPending ? 'Deleting…' : 'Delete'}
        cancelLabel="Cancel"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          deleteMutation.mutate(draft.workingHourId, {
            onSuccess: () => {
              setConfirmDeleteOpen(false);
              onOpenChange(false);
            },
          });
        }}
      />

      {/* F-PRS-117: disable-location appointment-count warning. Shown when an
       *  Edit-mode save disables a location that still has upcoming
       *  appointments. Verbatim copy from Flutter
       *  `doctor_schedule_tab.dart:516-532` (incl. the "If you disable." and
       *  " ," quirks, and the lowercased location names). Sibling of the
       *  delete AlertDialog (same focus-trap nesting rule). */}
      <ModalYesNo
        open={disableLocConfirm !== null}
        onCancel={() => setDisableLocConfirm(null)}
        title={
          disableLocationWarningCopy(
            disableLocConfirm?.names ?? '',
            disableLocConfirm?.count ?? 0,
          ).title
        }
        message={
          disableLocationWarningCopy(
            disableLocConfirm?.names ?? '',
            disableLocConfirm?.count ?? 0,
          ).message
        }
        confirmLabel="Yes"
        cancelLabel="No"
        loading={isMutating}
        onConfirm={() => {
          setDisableLocConfirm(null);
          doSave();
        }}
      />
    </>
  );
}

// ── Field components ───────────────────────────────────────────────────

function PracticeReadOnly({ profile }: { profile: DoctorWorkingHour }) {
  return (
    <div className="rounded-md bg-blue-100 px-3 py-2 text-sm">
      <p className="font-semibold text-blue-700">{profile.practiceName}</p>
      <p className="text-xs text-text-muted mt-1">
        Practice is fixed at profile creation. To assign this practitioner
        to a different practice, create a new working-hours profile.
      </p>
    </div>
  );
}

/**
 * Practice picker for Add mode — Flutter parity:
 * `doctor_working_hours_form_widget.dart:82-120` (the FieldAndLabel
 * dropdown). Renders the practitioner-scoped practice list.
 *
 * Empty-state copy is shown when the practitioner has no practices
 * assigned — the parent surface will rarely hit this since the user
 * needs at least one practice assignment to reach the Schedule tab,
 * but defending against it keeps the picker from rendering an empty
 * silent dropdown.
 */
function PracticeField({
  value,
  options,
  onChange,
}: {
  value: string;
  options: PracticeOption[];
  onChange: (practiceId: string) => void;
}) {
  const valid = options.filter(
    (o): o is { practiceSetupId: string; practiceName: string | null } =>
      !!o.practiceSetupId,
  );
  return (
    <div>
      <FieldLabel stacked>
        Practice <span className="text-status-error">*</span>
      </FieldLabel>
      {valid.length === 0 ? (
        <p className="text-sm text-text-muted">
          No practices assigned to this practitioner.
        </p>
      ) : (
        <Select
          value={value || ''}
          onChange={(v) => onChange(v ?? '')}
          placeholder="Select practice"
          options={valid.map((p) => ({
            label: p.practiceName ?? '—',
            value: p.practiceSetupId,
          }))}
        />
      )}
    </div>
  );
}

function NameField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <FieldLabel stacked>
        Profile name <span className="text-status-error">*</span>
      </FieldLabel>
      <TextField
        value={value}
        placeholder="e.g. Work, Vacation"
        maxLength={120}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function TypeField({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <FieldLabel stacked>
        Type <span className="text-status-error">*</span>
      </FieldLabel>
      <Select
        value={value?.toString() ?? ''}
        onChange={(v) => v && onChange(Number(v))}
        placeholder="Select type"
        options={DOCTOR_WH_TYPES.map((t) => ({
          label: t.name,
          value: t.value.toString(),
        }))}
      />
    </div>
  );
}

/**
 * Location (service type) multi-select — Flutter parity
 * (`doctor_working_hours_form_widget.dart:557`). One toggle row per org
 * location; "on" = the location is present in the draft's `locations[]`.
 * Verbatim subtitle copy from Flutter. Mandatory marker + the Save-gate
 * (≥1 enabled) live in the parent.
 */
function LocationsField({
  options,
  enabledIds,
  onToggle,
}: {
  options: OrgLocation[];
  enabledIds: Set<string>;
  onToggle: (loc: OrgLocation, on: boolean) => void;
}) {
  return (
    <div className="border-t border-border-hairline pt-4">
      <FieldLabel stacked>
        Location (service type) <span className="text-status-error">*</span>
      </FieldLabel>
      <p className="mt-1 mb-3 text-sm text-text-muted">
        Select all locations (service types) where this practitioner will provide
        services at. We will set up the respective appointment profiles and
        calendar slots for them.
      </p>
      {options.length === 0 ? (
        <p className="text-sm text-text-muted">
          No organisation locations configured.
        </p>
      ) : (
        <ul className="space-y-2">
          {options.map((loc) => (
            <li key={loc.id} className="flex items-center justify-between">
              <span className="text-sm text-text-default">{loc.name}</span>
              <Switch
                checked={enabledIds.has(loc.id)}
                onCheckedChange={(on) => onToggle(loc, on === true)}
                aria-label={`Provide services at ${loc.name}`}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Date input bound to the canonical no-Z ISO format. The HTML date
 * input gives us `YYYY-MM-DD`; we extend it to the millisecond-
 * precision form Flutter writes (`T00:00:00.000`) on every change
 * so the wire shape never sees a half-formed value. On read of an
 * existing record we slice the date portion off the no-Z ISO string.
 */
function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  // GET single may ship at second-precision (`T00:00:00`); PUT body
  // expects millisecond-precision (`T00:00:00.000`). Slicing the
  // first 10 chars normalises both shapes for the date input.
  const dateOnly = value ? value.slice(0, 10) : '';
  return (
    <div>
      <FieldLabel stacked>
        {label} <span className="text-status-error">*</span>
      </FieldLabel>
      <DatePickerField
        value={dateOnly}
        onChange={(v) => {
          // Write back at millisecond precision per Flutter's
          // `formatLocalDatetimeForWire`. Empty input → empty string
          // (Flutter convention; backend rejects null).
          onChange(v ? `${v}T00:00:00.000` : '');
        }}
      />
    </div>
  );
}

// ── Per-day slot editor ────────────────────────────────────────────────

function DayGroup({
  weekday,
  slots,
  onChange,
}: {
  weekday: string;
  slots: DoctorWorkingHourSlot[];
  onChange: (next: DoctorWorkingHourSlot[]) => void;
}) {
  const isDayOff = slots.length === 0 || slots.every((s) => s.dayOff);

  // All three handlers route through the pure helpers in
  // `working-hours-slot-helpers.ts` so the id-null-vs-GUID and
  // day-off-preservation contracts are unit-tested independently of
  // React render machinery (per @samer PR #143 review).
  const handleToggleDayOff = (next: boolean) => {
    onChange(applyDayOffToggle(slots, weekday, next));
  };

  const handleAddSlot = () => {
    onChange([...slots, makeEmptySlot(weekday)]);
  };

  const handleSlotChange = (
    index: number,
    patch: Partial<DoctorWorkingHourSlot>,
  ) => {
    onChange(slots.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const handleSlotRemove = (index: number) => {
    onChange(applySlotRemove(slots, index));
  };

  // Filter out isDeleted: true entries for rendering (they ride the
  // array silently).
  const visibleSlots = slots.filter((s) => !s.isDeleted);

  return (
    <div data-slot="hours-day" className="flex items-start gap-3 py-2 border-b border-border-hairline last:border-b-0">
      <div className="w-14 pt-2 flex items-center gap-2">
        <label data-slot="hours-day-name" className="text-sm font-semibold text-text-default">
          {weekday.slice(0, 3)}
        </label>
      </div>
      <div className="flex-1 space-y-2">
        <label className="inline-flex items-center gap-2 text-sm text-text-muted">
          <input
            type="checkbox"
            checked={isDayOff}
            onChange={(e) => handleToggleDayOff(e.target.checked)}
            className="h-4 w-4"
          />
          Day off
        </label>
        {!isDayOff && (
          <>
            {visibleSlots.map((slot, i) => (
              <DaySlotRow
                key={slot.id || `slot-new-${weekday}-${i}`}
                slot={slot}
                onChange={(patch) =>
                  handleSlotChange(slots.indexOf(slot), patch)
                }
                onRemove={() => handleSlotRemove(slots.indexOf(slot))}
              />
            ))}
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              data-slot="hours-add"
              onClick={handleAddSlot}
              className="text-brand-primary text-sm h-8"
            >
              <Plus className="size-4 mr-1" />
              Add Slot
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function DaySlotRow({
  slot,
  onChange,
  onRemove,
}: {
  slot: DoctorWorkingHourSlot;
  onChange: (patch: Partial<DoctorWorkingHourSlot>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1">
        <HourSelect
          value={slot.startHour}
          onChange={(v) => onChange({ startHour: v })}
        />
        <MinuteSelect
          value={slot.startMinute}
          onChange={(v) => onChange({ startMinute: v })}
        />
      </div>
      <span className="text-sm text-text-muted">to</span>
      <div className="flex items-center gap-1">
        <HourSelect
          value={slot.endHour}
          onChange={(v) => onChange({ endHour: v })}
        />
        <MinuteSelect
          value={slot.endMinute}
          onChange={(v) => onChange({ endMinute: v })}
        />
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove slot"
        className="icon-action"
      >
        <Trash2 className="size-4" aria-hidden />
      </button>
    </div>
  );
}

// ── Hour / minute pickers ─────────────────────────────────────────────

function HourSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Select
      aria-label="Hour"
      size="compact"
      className="w-18"
      value={value}
      onChange={(v) => v && onChange(v)}
      placeholder="Hour"
      options={HOURS_LIST.map((h) => ({ label: h, value: h }))}
    />
  );
}

function MinuteSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Select
      aria-label="Minute"
      size="compact"
      className="w-18"
      value={value}
      onChange={(v) => v && onChange(v)}
      placeholder="Min"
      options={MINUTES_LIST.map((m) => ({ label: m, value: m }))}
    />
  );
}

